import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import systemSettingsApi, {
  HospitalConfig,
  SystemConfig,
  RecordingLimits,
  PatientIdFormat,
} from '@/services/api/systemSettingsApi';
import { toast } from 'sonner';
import { extractErrorMessage } from '@/utils/errorHandling';
import { useAuthStore } from '@/stores/useAuthStore';

// Query keys factory. Scoped by the active tenant so switching clinics in the
// same browser (or holding tabs open on different tenant hosts) never serves
// one tenant's cached settings under another — the symptom behind "settings
// only changed on the subdomain I'm on".
export const systemSettingsKeys = {
  all: ['system-settings'] as const,
  hospitalConfig: (tenantId: string | null | undefined) =>
    [...systemSettingsKeys.all, 'hospital-config', tenantId ?? ''] as const,
  systemConfig: (tenantId: string | null | undefined) =>
    [...systemSettingsKeys.all, 'system-config', tenantId ?? ''] as const,
  recordingLimits: (tenantId: string | null | undefined) =>
    [...systemSettingsKeys.all, 'recording-limits', tenantId ?? ''] as const,
  patientIdFormat: (tenantId: string | null | undefined) =>
    [...systemSettingsKeys.all, 'patient-id-format', tenantId ?? ''] as const,
};

/** Server default mirrors SystemSettingsService.getPatientIdFormat(). */
export const DEFAULT_PATIENT_ID_FORMAT: PatientIdFormat = {
  prefix: 'P-',
  strategy: 'sequential',
};

/** Server defaults mirror SystemSettingsService.getRecordingLimits(). */
export const DEFAULT_RECORDING_LIMITS: RecordingLimits = {
  maxRecordingMinutes: 60,
  silenceTimeoutMinutes: 5,
};

/**
 * Per-tenant recording limits (max session length + silence auto-terminate).
 * Always resolves to concrete numbers — falls back to defaults before the
 * query lands or if it errors, so the recording flow never blocks on it.
 */
export const useRecordingLimits = (): RecordingLimits => {
  const tenantId = useAuthStore((s) => s.activeTenantId);
  const { data } = useQuery({
    queryKey: systemSettingsKeys.recordingLimits(tenantId),
    queryFn: () => systemSettingsApi.getRecordingLimits(),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
  return data?.data ?? DEFAULT_RECORDING_LIMITS;
};

/** Full query form (loading/error) for the settings UI. */
export const useRecordingLimitsQuery = () => {
  const tenantId = useAuthStore((s) => s.activeTenantId);
  return useQuery({
    queryKey: systemSettingsKeys.recordingLimits(tenantId),
    queryFn: () => systemSettingsApi.getRecordingLimits(),
    staleTime: 5 * 60_000,
  });
};

/** Update the per-tenant recording limits (admin only). */
export const useUpdateRecordingLimits = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RecordingLimits) =>
      systemSettingsApi.updateRecordingLimits(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: systemSettingsKeys.all,
      });
      toast.success('Recording limits updated');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to update recording limits');
    },
  });
};

/** Full query form for the patient-ID format (owner/admin settings UI). */
export const usePatientIdFormatQuery = () => {
  const tenantId = useAuthStore((s) => s.activeTenantId);
  return useQuery({
    queryKey: systemSettingsKeys.patientIdFormat(tenantId),
    queryFn: () => systemSettingsApi.getPatientIdFormat(),
    staleTime: 5 * 60_000,
  });
};

/** Update the per-tenant patient-ID (MRN) format (admin only). */
export const useUpdatePatientIdFormat = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PatientIdFormat) =>
      systemSettingsApi.updatePatientIdFormat(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: systemSettingsKeys.all });
      toast.success('Patient ID format updated');
    },
    onError: (error) => {
      toast.error(
        extractErrorMessage(error) || 'Failed to update patient ID format',
      );
    },
  });
};

/**
 * Get hospital configuration (public - no authentication required)
 */
export const useHospitalConfig = () => {
  const tenantId = useAuthStore((s) => s.activeTenantId);
  return useQuery({
    queryKey: systemSettingsKeys.hospitalConfig(tenantId),
    queryFn: () => systemSettingsApi.getHospitalConfig(),
    // Short window + refetch on focus so an OWNER's branding/settings change
    // propagates to every other user (and the clinic's other hosts) on their
    // next focus/reload, not up to 5 minutes later.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
};

/**
 * Create hospital configuration (requires authentication)
 */
export const useCreateHospitalConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: HospitalConfig) => systemSettingsApi.createHospitalConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: systemSettingsKeys.all });
      queryClient.invalidateQueries({ queryKey: ['theme-config'] });
      toast.success('Hospital configuration created successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to create hospital configuration');
    },
  });
};

/**
 * Update hospital configuration (requires authentication)
 */
export const useUpdateHospitalConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: HospitalConfig) => systemSettingsApi.updateHospitalConfig(data),
    onSuccess: () => {
      // Invalidate every consumer of hospital config. The same backend
      // payload feeds both the system-settings forms (this hook's own
      // queryKey) and the global theme/branding loader (`['theme-config']`
      // in useTheme), which drives sidebar + header + login screen.
      // Without the second invalidation, branding changes don't show up
      // for up to 5 minutes (the theme query's stale window).
      queryClient.invalidateQueries({ queryKey: systemSettingsKeys.all });
      queryClient.invalidateQueries({ queryKey: ['theme-config'] });
      toast.success('Hospital configuration updated successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to update hospital configuration');
    },
  });
};

/**
 * Get system configuration (requires authentication).
 *
 * Pass `enabled: false` from a caller that hasn't actually surfaced the
 * system/backup tabs yet — the backend endpoint
 * (`/system-settings/frontend/system-config`) hasn't been implemented,
 * so unconditionally firing the query produced a noisy 404 in dev.
 */
export const useSystemConfig = (opts: { enabled?: boolean } = {}) => {
  const tenantId = useAuthStore((s) => s.activeTenantId);
  return useQuery({
    queryKey: systemSettingsKeys.systemConfig(tenantId),
    queryFn: () => systemSettingsApi.getSystemConfig(),
    staleTime: 60000, // 1 minute
    enabled: opts.enabled ?? false,
  });
};

/**
 * Create system configuration (requires authentication)
 */
export const useCreateSystemConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SystemConfig) => systemSettingsApi.createSystemConfig(data),
    onSuccess: () => {
      // Invalidate system config
      queryClient.invalidateQueries({ queryKey: systemSettingsKeys.all });
      toast.success('System configuration created successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to create system configuration');
    },
  });
};

/**
 * Update system configuration (requires authentication)
 */
export const useUpdateSystemConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SystemConfig) => systemSettingsApi.updateSystemConfig(data),
    onSuccess: () => {
      // Invalidate system config
      queryClient.invalidateQueries({ queryKey: systemSettingsKeys.all });
      toast.success('System configuration updated successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to update system configuration');
    },
  });
};
